import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { findFee } from "../../helpers/acquirer";
import {
    formatCurrencyToShow as formatCurrency,
    parseCurrencyToNumber,
} from "../../helpers/currency";
import { findAcquirer } from "../../helpers/customer";
import { percentageMask } from "../../helpers/masks/number";
import { useQuery } from "../../hooks/useQuery";
import { Acquirer } from "../../interfaces/acquirer";
import { ApiResponse } from "../../interfaces/paginated";
import { api } from "../../services/api";
import { acquirerFeeSchema } from "../../validators/schemas/acquirerFee";

type FormData = {
    pixFeePercentage?: string;
    pixFixedFee?: string;
    cardFeePercentage?: string;
    cardFixedFee?: string;
};

interface FeeEditModalProps {
    open: boolean;
    onClose: () => void;
    data: Acquirer | null;
    onSuccess?: (data: FormData) => void;
    onError?: (data: FormData, error: string) => void;
}

function FeeAdornment({ value }: { value?: string }) {
    return <InputAdornment position="end">{value}</InputAdornment>;
}

export function FeeEditModal({
    open,
    onClose,
    data,
    onSuccess,
    onError,
}: FeeEditModalProps) {
    const {
        handleSubmit,
        control,
        setValue,
        reset,
        formState: { errors, isSubmitting, isValid },
    } = useForm<FormData>({
        mode: "onChange",
        resolver: yupResolver(acquirerFeeSchema),
    });

    const handleClose = () => {
        onClose();
        reset();
    };

    const onSubmit = async (values: FormData) => {
        const paymentMethods = ["pix", "card"] as const;

        try {
            const fees = paymentMethods.map((method) => {
                const fee_percentage = values[`${method}FeePercentage`];
                const fixed_fee = values[`${method}FixedFee`];

                return {
                    payment_method: method,
                    fee_percentage: fee_percentage
                        ? Number(fee_percentage)
                        : null,
                    fixed_fee: fixed_fee
                        ? parseCurrencyToNumber(fixed_fee)
                        : null,
                };
            });

            const response = await api.put<ApiResponse<null>>(
                `acquirers/${data?.id}/fees`,
                { fees }
            );

            if (!response.data.status) {
                onError?.(values, response.data.message);
                return;
            }

            onSuccess?.(values);
        } catch (error: any) {
            onError?.(values, error?.response?.data?.message || error?.message);
        } finally {
            handleClose();
        }
    };

    useEffect(() => {
        if (data) {
            const paymentMethods = ["pix", "card"] as const;

            paymentMethods.forEach((method) => {
                const fee = findFee(data, method);
                setValue(
                    `${method}FeePercentage`,
                    percentageMask(fee?.fee_percentage)
                );
                setValue(
                    `${method}FixedFee`,
                    formatCurrency(fee?.fixed_fee) || ""
                );
            });
        }

        return () => reset();
    }, [data]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            disableRestoreFocus
        >
            <DialogTitle variant="h6" component="h2">
                {data?.name}
            </DialogTitle>

            <DialogContent>
                <Box
                    gap={2}
                    display="flex"
                    flexDirection="column"
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                >
                    <Typography variant="subtitle2" fontWeight={600} mt={1}>
                        Configuração PIX
                    </Typography>

                    <Box
                        display="flex"
                        gap={2}
                        flexDirection={{ xs: "column", sm: "row" }}
                    >
                        <Controller
                            name="pixFeePercentage"
                            control={control}
                            render={({ field: { onChange, ...field } }) => (
                                <TextField
                                    label="Taxa PIX (%)"
                                    size="small"
                                    fullWidth
                                    placeholder="Ex: 1.3"
                                    error={!!errors.pixFeePercentage}
                                    helperText={
                                        errors.pixFeePercentage?.message
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <FeeAdornment value="%" />
                                        ),
                                    }}
                                    onChange={(e) =>
                                        onChange(percentageMask(e.target.value))
                                    }
                                    {...field}
                                />
                            )}
                        />

                        <Controller
                            name="pixFixedFee"
                            control={control}
                            render={({ field: { onChange, ...field } }) => (
                                <TextField
                                    label="Taxa Fixa PIX"
                                    size="small"
                                    fullWidth
                                    placeholder="R$ 0.30"
                                    error={!!errors.pixFixedFee}
                                    helperText={errors.pixFixedFee?.message}
                                    InputProps={{
                                        endAdornment: (
                                            <FeeAdornment value="R$" />
                                        ),
                                    }}
                                    onChange={({ target: { value } }) => {
                                        const mv = formatCurrency(value, true);
                                        onChange(mv);
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </Box>

                    <Typography variant="subtitle2" fontWeight={600} mt={1}>
                        Configuração Cartão
                    </Typography>

                    <Box
                        display="flex"
                        gap={2}
                        flexDirection={{ xs: "column", sm: "row" }}
                    >
                        <Controller
                            name="cardFeePercentage"
                            control={control}
                            render={({ field: { onChange, ...field } }) => (
                                <TextField
                                    label="Taxa Cartão (%)"
                                    size="small"
                                    fullWidth
                                    placeholder="Ex: 2.5"
                                    error={!!errors.cardFeePercentage}
                                    helperText={
                                        errors.cardFeePercentage?.message
                                    }
                                    InputProps={{
                                        endAdornment: (
                                            <FeeAdornment value="%" />
                                        ),
                                    }}
                                    onChange={(e) =>
                                        onChange(percentageMask(e.target.value))
                                    }
                                    {...field}
                                />
                            )}
                        />

                        <Controller
                            name="cardFixedFee"
                            control={control}
                            render={({ field: { onChange, ...field } }) => (
                                <TextField
                                    label="Taxa Fixa Cartão"
                                    size="small"
                                    fullWidth
                                    placeholder="R$ 0.30"
                                    error={!!errors.cardFixedFee}
                                    helperText={errors.cardFixedFee?.message}
                                    InputProps={{
                                        endAdornment: (
                                            <FeeAdornment value="R$" />
                                        ),
                                    }}
                                    onChange={({ target: { value } }) => {
                                        const mv = formatCurrency(value, true);
                                        onChange(mv);
                                    }}
                                    {...field}
                                />
                            )}
                        />
                    </Box>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={2}
                    >
                        <Button onClick={handleClose} color="inherit">
                            Cancelar
                        </Button>
                        <LoadingButton
                            variant="contained"
                            type="submit"
                            loading={isSubmitting}
                            disabled={!isValid}
                        >
                            Salvar
                        </LoadingButton>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
